import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {useClient} from '../core/client';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FREQUENCIES = ['Daily', 'Weekly', 'Bi-Weekly', 'Monthly'];

const createCheckins = ({ frequency, startDate, endDate, weeklyDays = [], biweeklyDay, monthlyDay }) => {
    if (!startDate || !endDate || startDate.isAfter(endDate)) return [];
    const results = [];
    let cursor = startDate.startOf('day');
    const end = endDate.endOf('day');
    const pushCheckin = (date) => {
        results.push({
            id: results.length,
            date: date.toISOString(),
            isCompleted: false,
            status: 'Pending',
            notes: '',
            photoUrl: '',
        });
    };

    switch (frequency) {
        case 'Weekly': {
            const selected = new Set(weeklyDays);
            while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
                if (selected.has(cursor.format('ddd'))) {
                    pushCheckin(cursor);
                }
                cursor = cursor.add(1, 'day');
            }
            break;
        }
        case 'Bi-Weekly': {
            if (!biweeklyDay) break;
            cursor = cursor.day(WEEKDAYS.indexOf(biweeklyDay));
            if (cursor.isBefore(startDate)) cursor = cursor.add(1, 'week');
            while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
                pushCheckin(cursor);
                cursor = cursor.add(2, 'week');
            }
            break;
        }
        case 'Monthly': {
            const dayOfMonth = monthlyDay || 1;
            cursor = cursor.date(dayOfMonth);
            if (cursor.isBefore(startDate)) cursor = cursor.add(1, 'month').date(dayOfMonth);
            while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
                pushCheckin(cursor);
                cursor = cursor.add(1, 'month').date(dayOfMonth);
            }
            break;
        }
        default: {
            while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
                pushCheckin(cursor);
                cursor = cursor.add(1, 'day');
            }
        }
    }
    return results;
};

const CheckinScheduleEditor = ({ open, onClose, defendantId, schedule, onScheduleGenerated }) => {
    const [status, setStatus] = useState('ACTIVE');
    const [frequency, setFrequency] = useState('Daily');
    const [weeklyDays, setWeeklyDays] = useState([]);
    const [biweeklyDay, setBiweeklyDay] = useState('Mon');
    const [monthlyDay, setMonthlyDay] = useState(1);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [autoStop, setAutoStop] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const client = useClient();

    useEffect(() => {
        if (schedule) {
            setStatus(schedule.status || 'ACTIVE');
            setFrequency(schedule.frequency || 'Daily');
            setWeeklyDays(schedule.weeklyDays || schedule.checkinDays || []);
            setBiweeklyDay(schedule.biweeklyDay || 'Mon');
            setMonthlyDay(schedule.monthlyDay || 1);
            setStartDate(schedule.startDate ? dayjs(schedule.startDate) : null);
            setEndDate(schedule.endDate ? dayjs(schedule.endDate) : null);
            setAutoStop(!!schedule.endDate);
        } else {
            setStatus('ACTIVE');
            setFrequency('Daily');
            setWeeklyDays([]);
            setBiweeklyDay('Mon');
            setMonthlyDay(1);
            setStartDate(null);
            setEndDate(null);
            setAutoStop(false);
        }
    }, [schedule, open]);

    useEffect(() => {
        if (!autoStop) {
            setEndDate(null);
        }
    }, [autoStop]);

    const formValid = useMemo(() => {
        if (!startDate) return false;
        if (autoStop && (!endDate || startDate.isAfter(endDate))) return false;
        if (frequency === 'Weekly' && weeklyDays.length === 0) return false;
        return true;
    }, [frequency, startDate, endDate, weeklyDays, autoStop]);

    const previewCheckins = useMemo(() => {
        if (status !== 'ACTIVE') return [];
        if (!formValid) return [];
        const previewEnd = autoStop && endDate ? endDate : startDate.add(90, 'day');
        const checkins = createCheckins({
            frequency,
            startDate,
            endDate: previewEnd,
            weeklyDays,
            biweeklyDay,
            monthlyDay,
        });
        return checkins.slice(0, 8);
    }, [status, formValid, frequency, startDate, endDate, autoStop, weeklyDays, biweeklyDay, monthlyDay]);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formValid) return;
        setSaving(true);
        setError(null);
        const checkins = autoStop && endDate
            ? createCheckins({
                frequency,
                startDate,
                endDate,
                weeklyDays,
                biweeklyDay,
                monthlyDay,
            })
            : [];
        try {
            const payload = {
                status,
                frequency,
                startDate: startDate?.toISOString(),
                endDate: autoStop && endDate ? endDate.toISOString() : null,
                checkins: checkins.map(({ date, isCompleted, status: checkinStatus, notes, photoUrl }) => ({
                    date,
                    isCompleted,
                    status: checkinStatus,
                    notes,
                    photoUrl,
                })),
            };
            if (!schedule?.id) {
                payload.defendantId = defendantId;
            }
            const endpoint = schedule?.id ? `/checkin-schedule/${schedule.id}` : '/checkin-schedule';
            const request = schedule?.id
                ? client.put(endpoint, payload)
                : client.post(endpoint, payload);
            const { data } = await request;
            await onScheduleGenerated?.(data);
            if (autoStop && endDate) {
                alert(`This schedule will automatically stop on ${endDate.format('MMM D, YYYY')}`);
            }
        } catch (err) {
            console.error('Failed to save check-in schedule', err);
            setError('Unable to save schedule. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Edit Check-in Schedule
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <FormControl component="fieldset">
                                <FormLabel>Schedule Status</FormLabel>
                                <RadioGroup
                                    row
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                >
                                    <FormControlLabel value="ACTIVE" control={<Radio />} label="Active" />
                                    <FormControlLabel value="INACTIVE" control={<Radio />} label="Inactive" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Box component="form" onSubmit={handleSave} sx={{ mt: 1 }}>
                                <Grid container spacing={3}>

                                    <Grid item xs={12}>
                                        <DatePicker
                                            label="Start Date"
                                            value={startDate}
                                            onChange={setStartDate}
                                            slotProps={{ textField: { fullWidth: true, required: true } }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl component="fieldset">
                                            <FormLabel>Frequency</FormLabel>
                                            <RadioGroup
                                                row
                                                value={frequency}
                                                onChange={(e) => setFrequency(e.target.value)}
                                            >
                                                {FREQUENCIES.map((freq) => (
                                                    <FormControlLabel
                                                        key={freq}
                                                        value={freq}
                                                        control={<Radio />}
                                                        label={freq}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>

                                    {frequency === 'Weekly' && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Weekly Days
                                            </Typography>
                                            <Box>
                                                <ToggleButtonGroup
                                                    value={weeklyDays}
                                                    onChange={(_, newDays) => setWeeklyDays(newDays)}
                                                    aria-label="weekly days"
                                                    exclusive={false}
                                                    sx={{ flexWrap: 'wrap', gap: 1 }}
                                                >
                                                    {WEEKDAYS.map((day) => (
                                                        <ToggleButton key={day} value={day} aria-label={day} size="small">
                                                            {day}
                                                        </ToggleButton>
                                                    ))}
                                                </ToggleButtonGroup>
                                                {weeklyDays.length === 0 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                        Select at least one day.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    )}

                                    {frequency === 'Bi-Weekly' && (
                                        <Grid item xs={12}>
                                            <TextField
                                                select
                                                fullWidth
                                                label="Bi-weekly Day"
                                                value={biweeklyDay}
                                                onChange={(e) => setBiweeklyDay(e.target.value)}
                                            >
                                                {WEEKDAYS.map((day) => (
                                                    <MenuItem key={day} value={day}>
                                                        {day}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    )}

                                    {frequency === 'Monthly' && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Day of Month"
                                                inputProps={{ min: 1, max: 31 }}
                                                value={monthlyDay}
                                                onChange={(e) => setMonthlyDay(Number(e.target.value))}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Stack spacing={3} sx={{ mt: 1 }}>
                                <Box>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        onChange={setEndDate}
                                        disabled={!autoStop}
                                        slotProps={{ textField: { fullWidth: true, required: autoStop } }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={autoStop}
                                                onChange={(e) => setAutoStop(e.target.checked)}
                                            />
                                        }
                                        label="Automatically stop on a specific date"
                                        sx={{ mt: 1 }}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Upcoming Check-ins
                                    </Typography>
                                    {status !== 'ACTIVE' && (
                                        <Typography variant="body2" color="text.secondary">
                                            This schedule is inactive.
                                        </Typography>
                                    )}
                                    {status === 'ACTIVE' && previewCheckins.length === 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            Add a start date and frequency to preview upcoming check-ins.
                                        </Typography>
                                    )}
                                    {status === 'ACTIVE' && previewCheckins.length > 0 && (
                                        <Stack spacing={1}>
                                            {previewCheckins.map((checkin) => (
                                                <Typography key={checkin.id} variant="body2">
                                                    {dayjs(checkin.date).format('MMM D, YYYY')}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </LocalizationProvider>
            </DialogContent>
            <DialogActions>
                {error && (
                    <Typography color="error" sx={{ flexGrow: 1, mr: 2 }}>
                        {error}
                    </Typography>
                )}
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!formValid || saving}
                    sx={{ color: 'common.white' }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CheckinScheduleEditor;
